import api from "./api";

export interface GovIssue {
  field: string;
  message: string;
  blocking: boolean;
}

export interface GovPreview {
  baseUrl: string;
  isProduction: boolean;
  productionAllowed: boolean;
  canTransmit: boolean;
  issues: GovIssue[];
  items?: { number: number; product?: string; netWeight?: number | null; productCatalog: string; attributesRequired?: number | null; attributesFilled?: number | null; attributesMissing?: string[]; taxStatus?: string | null; taxMissing?: string[] }[];
  exporterCatalog?: string;
  duimpPayload?: string;
  currentDuimp?: string;
}

export interface GovResult {
  success: boolean;
  messages: string[];
  number?: string;
  version?: string;
  response?: string;
}

const base = (id: string) => `/api/process/${id}/registration`;

export const getRegistrationPreview = async (id: string): Promise<GovPreview> =>
  (await api.get(`${base(id)}/preview`)).data;

export const transmitDuimp = async (id: string): Promise<GovResult> => (await api.post(`${base(id)}/duimp`)).data;

export const requestLpco = async (id: string, modelCode: string): Promise<GovResult> =>
  (await api.post(`${base(id)}/lpco`, { modelCode })).data;

/** Baixa o XML (DI/LI) para upload no Siscomex Importação. Lança Error com as mensagens de validação em 400. */
export const downloadLegacyXml = async (id: string, kind: "di" | "li") => {
  try {
    const response = await api.get(`${base(id)}/xml/${kind}`, { responseType: "blob" });
    const disposition = response.headers["content-disposition"] ?? "";
    const name = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? `${kind.toUpperCase()}.xml`;
    const url = URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (e: any) {
    const blob: Blob | undefined = e?.response?.data;
    if (blob instanceof Blob) {
      try {
        const body = JSON.parse(await blob.text());
        throw new Error((body.messages ?? []).join("\n") || "Falha ao gerar XML.");
      } catch (inner) {
        if (inner instanceof Error && !(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw new Error("Falha ao gerar XML.");
  }
};

/* ---- Diagnóstico da DUIMP + LPCO por modelo ---- */

export interface GovDiagnosisResult {
  type: string;
  item?: number | null;
  code: string;
  text: string;
  source?: string;
}

export interface GovRequiredLpco {
  modelCode: string;
  name: string;
  organ?: string;
  items: number[];
  status?: string;
  lpcoNumber?: string;
}

export interface GovDiagnosis {
  success: boolean;
  error?: string;
  number?: string;
  version?: string;
  duimpStatus?: string;
  status?: string;
  generatedAt?: string;
  results: GovDiagnosisResult[];
  requiredLpcos: GovRequiredLpco[];
}

export interface LpcoDomain {
  id: string;
  description: string;
}

export interface LpcoField {
  code: string;
  name: string;
  type: string;
  required: boolean;
  multiple: boolean;
  maxLength?: number | null;
  decimals?: number | null;
  mask?: string | null;
  unit?: string | null;
  domains: LpcoDomain[];
  condition?: string | null;
  parentCode?: string | null;
  guidance?: string | null;
  sub: LpcoField[];
}

export interface LpcoModelDef {
  code: string;
  name: string;
  organ?: string;
  info?: string;
  requiresProduct: boolean;
  formFields: LpcoField[];
  ncmDomain: string[];
  ncmFields: LpcoField[];
}

/** Valor simples = string (ou string[]); composto = objeto interno do valorComposto. */
export type LpcoValue = string | string[] | Record<string, any>;
export interface LpcoValues {
  form: Record<string, LpcoValue>;
  items: Record<string, Record<string, LpcoValue>>;
}

export interface LpcoForm {
  success: boolean;
  error?: string;
  model?: LpcoModelDef;
  values?: LpcoValues;
  auto?: LpcoValues;
  issues: GovIssue[];
  items: { number: number; ncm?: string; description?: string; product?: string }[];
  status: string;
  lpcoNumber?: string;
  submittedAt?: string;
  payload?: string;
  canSubmit: boolean;
}

export const getDiagnosis = async (id: string): Promise<GovDiagnosis> => (await api.get(`${base(id)}/diagnosis`)).data;

export const getLpcoForm = async (id: string, model: string): Promise<LpcoForm> =>
  (await api.get(`${base(id)}/lpco/${encodeURIComponent(model)}`)).data;

export const saveLpcoValues = async (id: string, model: string, values: LpcoValues): Promise<LpcoForm> =>
  (await api.put(`${base(id)}/lpco/${encodeURIComponent(model)}`, { values })).data;

export const submitLpco = async (id: string, model: string, values: LpcoValues): Promise<GovResult> =>
  (await api.post(`${base(id)}/lpco/${encodeURIComponent(model)}/submit`, { values })).data;

export const refreshLpco = async (id: string, model: string): Promise<GovResult> =>
  (await api.post(`${base(id)}/lpco/${encodeURIComponent(model)}/refresh`)).data;

/* ---- Dossiê da DUIMP (anexação eletrônica e-Docex) ---- */

export interface DossierKeyword {
  code: number;
  value: string;
}

export interface DossierInstruction {
  typeId: number;
  typeName: string;
  keywords: DossierKeyword[];
  attached: boolean;
  attachment?: { portalDocumentId: number; fileName: string; attachedAt: string; attachedBy: string } | null;
  suggestedDocumentId?: string | null;
}

export interface DossierPortalDocument {
  id: number;
  typeId: number;
  typeName: string;
  fileName: string;
  attachedAt?: string | null;
  keywords: string[];
}

export interface DossierInfo {
  duimpNumber?: string | null;
  canAttach: boolean;
  message?: string | null;
  instructions: DossierInstruction[];
  storedDocuments: { id: string; name: string }[];
  portalOk: boolean;
  portalError?: string | null;
  portalDocuments: DossierPortalDocument[];
  environment: string;
}

export const getDossier = async (id: string): Promise<DossierInfo> => (await api.get(`${base(id)}/dossier`)).data;

/** Anexa ao dossiê: documentId (arquivo já guardado no processo) OU file (upload). */
export const attachToDossier = async (
  id: string,
  p: { typeId: number; keywords: DossierKeyword[]; documentId?: string; file?: File }
): Promise<GovResult & { portalDocumentId?: number }> => {
  const form = new FormData();
  form.append("tipoDocumento", String(p.typeId));
  form.append("palavrasChave", JSON.stringify(p.keywords));
  if (p.file) form.append("file", p.file);
  else if (p.documentId) form.append("documentId", p.documentId);
  return (await api.post(`${base(id)}/dossier`, form)).data;
};

// ---- Dados da DUIMP (ajustes por processo + padrões globais) ----
export interface RegAddress {
  logradouro?: string | null;
  nomeCidade?: string | null;
  cep?: string | null;
  codigoSubdivisaoPais?: string | null;
  tin?: string | null;
  email?: string | null;
}

export interface RegItemSettings {
  tipoAplicacao?: string | null;
  condicao?: string | null;
  indicadorCompradorVendedor?: string | null;
  indicadorExportadorFabricante?: string | null;
  metodoValoracao?: number | null;
  coberturaCambial?: string | null;
  motivoSemCobertura?: number | null;
  instituicaoFinanciadora?: number | null;
  numeroRof?: string | null;
  valorCobertura?: string | null;
  manufacturerName?: string | null;
  manufacturerCountry?: string | null;
  manufacturerAddress?: RegAddress | null;
}

export interface RegKeyword {
  code: number;
  value: string;
}

export interface RegDocument {
  type: string;
  keywords: RegKeyword[];
}

export interface RegDomainOption {
  code: string;
  label: string;
}

export interface RegDomains {
  tipoAplicacao: RegDomainOption[];
  condicao: RegDomainOption[];
  compradorVendedor: RegDomainOption[];
  exportadorFabricante: RegDomainOption[];
  cobertura: RegDomainOption[];
}

export interface RegProcessSettings {
  declaredUnitCode?: string | null;
  consumptionUf?: string | null;
  consumptionCityIbge?: string | null;
  documents?: RegDocument[] | null;
  exporterAddress?: RegAddress | null;
  itemDefaults: RegItemSettings;
  items: Record<string, RegItemSettings>;
}

export interface RegSettingsView {
  values: RegProcessSettings;
  items: { number: number; product?: string; ncm?: string; effective: RegItemSettings }[];
  exporterName?: string;
  exporterRegistered: boolean;
  domains: RegDomains;
}

export interface RegDefaultsModel {
  declaredUnitCode?: string | null;
  consumptionUf?: string | null;
  consumptionCityIbge?: string | null;
  documents?: RegDocument[] | null;
  itemDefaults: RegItemSettings;
}

export interface RegDefaultsView {
  values: RegDefaultsModel;
  configFallback: RegDefaultsModel;
  domains: RegDomains;
}

const apiErrors = (e: any): never => {
  const errs: string[] | undefined = e?.response?.data?.errors;
  throw new Error(errs?.join("\n") || "Falha ao salvar.");
};

export const getRegistrationSettings = async (id: string): Promise<RegSettingsView> =>
  (await api.get(`${base(id)}/settings`)).data;

export const saveRegistrationSettings = async (id: string, values: RegProcessSettings): Promise<RegSettingsView> => {
  try {
    return (await api.put(`${base(id)}/settings`, values)).data;
  } catch (e) {
    return apiErrors(e);
  }
};

export const getRegistrationDefaults = async (): Promise<RegDefaultsView> =>
  (await api.get("/api/registration/defaults")).data;

export const saveRegistrationDefaults = async (values: RegDefaultsModel): Promise<RegDefaultsView> => {
  try {
    return (await api.put("/api/registration/defaults", values)).data;
  } catch (e) {
    return apiErrors(e);
  }
};
